import { z } from 'zod'

const nullableText = z.string().nullable()
const nullableBoolean = z.boolean().nullable()

const smsContactUnavailableReasonSchema = z.enum([
  'NO_PHONE',
  'SHARED_PHONE',
  'CONTACT_UNRESOLVED',
  'OPTED_OUT',
  'NO_SMS_RELATIONSHIP',
  'SMS_NOT_READY',
])

export const smsContactCapabilitySchema = z.discriminatedUnion('available', [
  z
    .object({
      available: z.literal(true),
      contactRef: z.string().regex(/^contact_v1_[a-f0-9]{64}$/),
      reason: z.literal('AVAILABLE'),
    })
    .strict(),
  z
    .object({
      available: z.literal(false),
      contactRef: z.null(),
      reason: smsContactUnavailableReasonSchema,
    })
    .strict(),
])

export const patientAddressSchema = z
  .object({
    line1: nullableText,
    line2: nullableText,
    city: nullableText,
    region: nullableText,
    postalCode: nullableText,
  })
  .strict()
  .nullable()

export const patientAppointmentSchema = z
  .object({
    appointmentId: z.string().min(1),
    scheduledAtMs: z.number().positive().nullable(),
    status: z.string().min(1),
    providerName: nullableText,
    appointmentTypeName: nullableText,
    operatoryName: nullableText,
  })
  .strict()

export const patientSchema = z
  .object({
    patientAuthorityId: z.string().regex(/^nhrlink_v1_[a-f0-9]{64}$/),
    providerPatientId: z.string().min(1),
    displayName: z.string().min(1).max(320),
    firstName: nullableText,
    lastName: nullableText,
    middleName: nullableText,
    phone: nullableText,
    email: nullableText,
    dateOfBirth: nullableText,
    gender: nullableText,
    address: patientAddressSchema,
    recordStatus: z.enum(['NEW', 'EXISTING', 'UNKNOWN']),
    source: z.enum(['FLIO_CREATED', 'NEXHEALTH']),
    providerLifecycleState: z.enum(['ACTIVE', 'INACTIVE', 'UNKNOWN']),
    providerNewPatient: nullableBoolean,
    providerNonPatient: nullableBoolean,
    primaryProviderId: nullableText,
    primaryProviderName: nullableText,
    foreignId: nullableText,
    foreignIdType: nullableText,
    chartId: nullableText,
    preferredLanguage: nullableText,
    guarantorId: nullableText,
    billingType: nullableText,
    providerCreatedAt: nullableText,
    providerLastSyncAt: nullableText,
    linkageState: z.string().min(1),
    interactionAggregateId: z
      .string()
      .regex(/^ia_v1_[a-f0-9]{64}$/)
      .nullable(),
    latestEngagement: z
      .object({
        latestActivityAtMs: z.number().positive(),
        latestPreview: z.string().min(1).max(500),
        latestChannel: z.enum(['SMS', 'VOICE', 'WEB_WIDGET', 'UNKNOWN']),
      })
      .strict()
      .nullable(),
    smsContactCapability: smsContactCapabilitySchema.optional(),
    lastUpdatedAtMs: z.number().positive().nullable(),
    appointments: z.array(patientAppointmentSchema),
  })
  .strict()

export const serviceIntentSchema = z
  .object({
    serviceIntent: z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/),
    serviceLabel: z.string().min(1).max(160),
  })
  .strict()

export const patientsResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    patients: z.array(patientSchema),
    serviceIntents: z.array(serviceIntentSchema),
    totalPatients: z.number().int().nonnegative(),
    pageSize: z.number().int().min(1).max(100),
    startIndex: z.number().int().nonnegative(),
    nextCursor: z.string().min(1).max(1024).nullable(),
  })
  .strict()

export const createPatientRequestSchema = z
  .object({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    phoneNumber: z
      .string()
      .trim()
      .regex(/^\+?[0-9]{8,15}$/),
    dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    email: z.string().trim().email().max(254),
    gender: z.enum(['Male', 'Female', 'Other']),
    serviceIntent: z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/),
  })
  .strict()

export const createPatientResponseSchema = z
  .object({
    outcome: z.enum(['MATCH', 'CREATED']),
    patientAuthorityId: z.string().regex(/^nhrlink_v1_[a-f0-9]{64}$/),
  })
  .strict()

export type PatientRecord = z.infer<typeof patientSchema>
export type PatientAppointment = z.infer<typeof patientAppointmentSchema>
export type SmsContactCapability = z.infer<typeof smsContactCapabilitySchema>
export type PatientsResponse = z.infer<typeof patientsResponseSchema>
export type ServiceIntent = z.infer<typeof serviceIntentSchema>
export type CreatePatientRequest = z.infer<typeof createPatientRequestSchema>
export type CreatePatientResponse = z.infer<typeof createPatientResponseSchema>
